/**
 * Admin layout route — guard role ∈ {admin, manager}.
 * Renders: AppSidebar (drawer on <lg) + topbar (user menu, logout) + Outlet.
 * Sidebar collapses to drawer at <lg per mobile-first spec (design-guideline §9).
 */
import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router';
import { User, Settings, LogOut } from 'lucide-react';
import { ThemeToggle } from '@/client/components/theme-toggle';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { AppSidebar } from '@client/components/app-sidebar';
import { authClient } from '@client/lib/auth-client';

export const Route = createFileRoute('/admin')({
  beforeLoad: async ({ navigate }) => {
    const { data: session } = await authClient.getSession();
    if (!session?.user) {
      throw navigate({ to: '/login', search: { redirect: '/admin' }, replace: true });
    }
    const role = (session.user as { role?: string }).role ?? '';
    if (role !== 'admin' && role !== 'manager') {
      throw navigate({ to: '/403', replace: true });
    }
    // Expose session data to child routes via context
    return { session };
  },
  component: AdminLayout,
});

function AdminLayout() {
  const navigate = useNavigate();
  const { session } = Route.useRouteContext();
  const user = session?.user as { name?: string; email?: string; role?: string } | undefined;
  const role = user?.role ?? '';
  const fullName = user?.name ?? user?.email ?? 'Người dùng';

  async function handleLogout() {
    await authClient.signOut();
    void navigate({ to: '/login', replace: true });
  }

  return (
    <SidebarProvider defaultOpen>
      <div className="flex min-h-screen w-full overflow-x-hidden">
        {/* Sidebar */}
        <AppSidebar role={role} fullName={fullName} />

        {/* Main area */}
        <div className="flex flex-1 flex-col min-w-0">
          {/* Topbar */}
          <header className="sticky top-0 z-30 flex h-14 items-center justify-between gap-2 border-b border-border bg-background/95 backdrop-blur px-3 md:px-4">
            {/* Hamburger — shows on <lg, sidebar handles its own open/close */}
            <div className="flex items-center gap-2">
              <SidebarTrigger aria-label="Menu" className="lg:hidden h-9 w-9" />
              {/* Brand text on mobile */}
              <span className="text-sm font-semibold text-foreground lg:hidden">CLC LMS</span>
            </div>

            <div className="flex items-center gap-1">
            <ThemeToggle />
            {/* User menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="flex items-center gap-2 h-9 px-2">
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-semibold shrink-0">
                    {fullName.charAt(0).toUpperCase()}
                  </div>
                  <span className="hidden sm:block text-sm truncate max-w-[120px]">{fullName}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-2 py-1.5">
                  <p className="text-xs font-medium truncate">{fullName}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <a href="/account" className="flex items-center gap-2 cursor-pointer">
                    <Settings className="h-4 w-4" />
                    Cài đặt tài khoản
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <a href="/account" className="flex items-center gap-2 cursor-pointer">
                    <User className="h-4 w-4" />
                    Hồ sơ
                  </a>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive flex items-center gap-2 cursor-pointer"
                  onClick={handleLogout}
                >
                  <LogOut className="h-4 w-4" />
                  Đăng xuất
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </div>
          </header>

          {/* Page content */}
          <main className="flex-1 p-4 md:p-6 min-w-0">
            <Outlet />
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
